import React, { createContext, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/app/store';
import { signIn } from 'src/slices/authSlice';
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
} from 'firebase/auth';
import {
  authentication,
  checkActiveUser,
  createNewActiveUser,
  removeUserInvitation,
} from 'src/config/firebaseConfig';
import { mapFirebaseUserToSerializedUser } from 'src/mappers/authMapper';
import { SerializedUser, UserProfile } from 'src/interfaces/user';
import { getInitData } from 'src/slices/appDataSlice';
import { User } from 'src/types/user';
import { setUserConfig } from 'src/config/sentryConfig';

interface AuthContextType {
  user: SerializedUser;
  signInWithGoogle: () => void;
  signUpWithGoogle: (userInvitation: User, activationCode: string) => void;
  signInWithEmail: (email: string, password: string) => void;
  signUpWithEmail: (
    userInput: {
      email: string;
      password: string;
      displayName: string;
    },
    userInvitation: User,
    activationCode: string,
  ) => void;
  isAuthenticated: boolean;
  resetAuth: () => void;
}

export const AuthContext = createContext<AuthContextType | null>({
  isAuthenticated: false,
  user: undefined,
  signInWithGoogle: function (): void {
    console.log("empty call to signInWithGoogle")
  },
  signUpWithGoogle: function (userInvitation: User, activationCode: string): void {
    console.log("empty call to signUpWithGoogle")
  },
  signInWithEmail: function (email: string, password: string): void {
    console.log("empty call to signInWithEmail")
  },
  signUpWithEmail: function (userInput: {
    email: string;
    password: string;
    displayName: string;
  }, userInvitation: User, activationCode: string): void {
    console.log("empty call to signUpWithEmail")
  },
  resetAuth: function (): void {
    console.log("empty call to resetAuth")
  }
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useDispatch();
  const user: UserProfile = useSelector((state: RootState) => state.auth.user);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false); // Track if `getInitData` has been called

  // console.log('AuthProvider--', user);
  const initializeData = useCallback(() => {
    if (!hasInitialized && user && user.dash?.enabled) {
      dispatch(getInitData({ userProfile: user }));
      setHasInitialized(true);
    }
  }, [dispatch, user, hasInitialized]);

  useEffect(() => {
    if (user && user.dash.enabled) {
      setIsAuthenticated(true);
      initializeData();
      setUserConfig(user);
    } else {
      setIsAuthenticated(false);
      setHasInitialized(false);
    }
  }, [user, initializeData]);

  const signInWithGoogle = async () => {
    // Logic to handle sign-in
    const result = await signInWithPopup(
      authentication,
      new GoogleAuthProvider(),
    ); // Replace with actual Google Sign-In logic
    if (result && result.user) {
      setIsAuthenticated(true);
      const snapshot = await checkActiveUser(result.user.uid);
      dispatch(signIn(snapshot));

      //dispatch(signIn(mapFirebaseUserToSerializedUser(result.user)));
    }
  };

  const signUpWithGoogle = async (
    userInvitation: User,
    activationCode: string,
  ) => {
    // Logic to handle sign-in
    const result = await signInWithPopup(
      authentication,
      new GoogleAuthProvider(),
    ); // Replace with actual Google Sign-In logic
    if (result && result.user) {
      setIsAuthenticated(true);
      const user = { ...userInvitation };
      if ('isForExistingUser' in user) {
        delete user.isForExistingUser;
      }
      user.displayName = result.user.displayName || '';
      const uid = result.user.uid;
      console.log('updated user', user);
      await createNewActiveUser(user, uid);
      await removeUserInvitation(activationCode);
      dispatch(signIn(user));
    }
  };

  const signUpWithEmail = async (
    userInput: {
      email: string;
      password: string;
      displayName: string;
    },
    userInvitation: User,
    activationCode: string,
  ) => {
    // Logic to handle sign-up
    const { email, password, displayName } = userInput;
    const result = await createUserWithEmailAndPassword(
      authentication,
      email,
      password,
    );
    if (result && result.user) {
      // Get the UID of the newly created user
      const uid = result.user.uid;

      // Step 2: Set the display name (name) for the user profile
      await updateProfile(result.user, {
        displayName: userInput.displayName,
      });
      const user = { ...userInvitation };
      if ('isForExistingUser' in user) {
        delete user.isForExistingUser;
      }
      user.displayName = userInput.displayName || '';
      console.log('updated user', user);
      setIsAuthenticated(true);
      const snapshot = await createNewActiveUser(user, uid);
      await removeUserInvitation(activationCode);
      dispatch(signIn(snapshot));
      //dispatch(signIn(mapFirebaseUserToSerializedUser(result.user)));
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    // Logic to handle sign-up
    const result = await signInWithEmailAndPassword(
      authentication,
      email,
      password,
    );
    if (result && result.user) {
      setIsAuthenticated(true);
      const snapshot = await checkActiveUser(result.user.uid);
      dispatch(signIn(snapshot));
      //dispatch(signIn(mapFirebaseUserToSerializedUser(result.user)));
    }
  };

  const resetAuth = () => {
    setIsAuthenticated(false);
    setHasInitialized(false);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        signInWithGoogle,
        signInWithEmail,
        signUpWithGoogle,
        signUpWithEmail,
        resetAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
